import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Stethoscope, MapPin, Star, TrendingUp, Users, Target, 
  CheckCircle, ArrowRight, Phone, Calendar, Heart,
  Shield, Lock, BarChart3, ClipboardList, Download, FileText
} from "lucide-react";

const HealthcareMarketingPage = () => {

  useEffect(() => {
    document.title = "Healthcare Marketing Dallas | Medical Practice SEO | Digital Media Ninja";
  }, []);

  const stats = [
    { value: "Patients", label: "Acquisition Strategy", icon: TrendingUp },
    { value: "HIPAA", label: "Aware Marketing", icon: Stethoscope },
    { value: "Booking", label: "Appointment Funnels", icon: Target },
    { value: "Reviews", label: "Reputation Systems", icon: Star }
  ];

  const services = [
    { icon: MapPin, title: "Medical SEO", desc: "Rank #1 for healthcare searches like 'doctor near me' and specialty-specific terms." },
    { icon: Lock, title: "HIPAA-Compliant Marketing", desc: "All strategies follow healthcare privacy regulations and industry best practices." },
    { icon: Star, title: "Patient Review Management", desc: "Build trust with authentic patient reviews on Google, Healthgrades, and Zocdoc." },
    { icon: ClipboardList, title: "Healthcare Directory Optimization", desc: "Optimize profiles on Healthgrades, Vitals, WebMD, and specialty directories." },
    { icon: Heart, title: "Patient Education Content", desc: "Authoritative health content that builds trust and attracts new patients." },
    { icon: BarChart3, title: "Appointment Tracking", desc: "Track patient acquisition from first click to scheduled appointment." }
  ];

  const practiceTypes = [
    "Primary Care", "Dental Practices", "Chiropractic", "Physical Therapy",
    "Dermatology", "Ophthalmology", "Orthopedics", "Mental Health",
    "Urgent Care", "Plastic Surgery", "OB/GYN", "Pediatrics"
  ];

  const serviceAreas = [
    "Dallas", "Fort Worth", "Plano", "Frisco", "McKinney",
    "Richardson", "Arlington", "Irving", "Denton"
  ];

  const faqs = [
    { 
      q: "Is healthcare marketing HIPAA compliant?", 
      a: "Yes, all our healthcare marketing strategies comply with HIPAA regulations. This includes proper handling of patient testimonials (with written consent), secure website forms with encrypted data transmission, compliant advertising practices, and careful management of patient information. We never use protected health information in marketing without proper authorization." 
    },
    { 
      q: "How do I get more patient reviews?", 
      a: "We implement automated, HIPAA-compliant review request systems that send follow-up messages after appointments. These are carefully worded to comply with healthcare regulations while encouraging satisfied patients to share their experience. We also train your staff on ethical review solicitation and help you respond professionally to all feedback." 
    },
    { 
      q: "What platforms should healthcare practices focus on?", 
      a: "Beyond Google (which is essential), healthcare practices should optimize Healthgrades, Zocdoc (for practices that accept it), Vitals, WebMD, and specialty-specific directories. For dentists, that includes Yelp and DentistryIQ. For specialists, focus on directories specific to your practice area. We create a comprehensive presence across all relevant platforms." 
    },
    { 
      q: "How can healthcare practices compete with large health systems?", 
      a: "Independent practices can compete by emphasizing personalized care, shorter wait times, and building strong local SEO presence. We help you rank for neighborhood-specific searches that large systems often overlook. Patient reviews highlighting your personalized approach often outweigh the brand recognition of larger systems." 
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Healthcare Marketing & Medical Practice SEO Services Dallas",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
          "telephone": "(432) 934-9470"
        },
        "areaServed": serviceAreas.map(city => ({ "@type": "City", "name": city })),
        "description": "HIPAA-compliant digital marketing for medical practices, dental offices, and healthcare providers in Dallas-Fort Worth. Medical SEO, patient review management, and healthcare content marketing.",
        "serviceType": "Healthcare Marketing Services"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black" data-testid="healthcare-marketing-page">
      <Helmet>
        <title>Healthcare Marketing Dallas | Medical Practice SEO | Digital Media Ninja</title>
        <meta name="description" content="HIPAA-compliant healthcare marketing and SEO services in Dallas. Attract new patients with medical practice SEO, patient review management, and healthcare content marketing. 150% avg patient increase." />
        <meta name="keywords" content="healthcare marketing Dallas, medical practice SEO, dental marketing DFW, doctor marketing Dallas, healthcare SEO Texas, medical advertising, patient acquisition" />
        <link rel="canonical" href="https://digitalmedianinja.com/healthcare-marketing" />
        <meta property="og:title" content="Healthcare Marketing Dallas | Medical Practice SEO" />
        <meta property="og:description" content="HIPAA-compliant healthcare marketing for Dallas practices. 150% average patient increase. SEO, reviews, content marketing." />
        <meta property="og:url" content="https://digitalmedianinja.com/healthcare-marketing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/10 via-transparent to-blue-500/10"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <Stethoscope className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">HIPAA-Compliant Healthcare Marketing</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Grow Your <span className="text-cyan-400">Healthcare Practice</span> in Dallas
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Attract more patients with HIPAA-compliant digital marketing. We help Dallas medical practices, dental offices, and healthcare providers build their patient base.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <BookConsultationButton meetingType="healthcare" size="lg">
                Get Free Healthcare Marketing Audit <ArrowRight className="w-5 h-5 ml-2" />
              </BookConsultationButton>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10"
                onClick={() => window.location.href='/blog/how-to-market-a-healthcare-practice'}
                data-testid="read-blog-btn"
              >
                <FileText className="w-5 h-5 mr-2" /> Read Our Full Guide
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.href='/guides/healthcare-marketing'}
                data-testid="read-playbook-btn"
              >
                <Download className="w-5 h-5 mr-2" /> Marketing Playbook
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <stat.icon className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Healthcare Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing designed specifically for healthcare providers, with full HIPAA compliance.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-cyan-400/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-cyan-400/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Types */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Healthcare Practices We Serve</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {practiceTypes.map((type, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center hover:border-cyan-400/30 transition-colors">
                <p className="text-white font-medium text-sm">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIPAA Badge */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center p-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl border border-cyan-500/20">
            <Shield className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-4">100% HIPAA Compliant Marketing</h3>
            <p className="text-gray-300">All our healthcare marketing strategies follow HIPAA regulations and healthcare industry best practices. Your patients' privacy is protected at every step.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Healthcare Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10">
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-cyan-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Grow Your Healthcare Practice?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Get a free healthcare marketing audit and custom strategy to attract more patients.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="healthcare" size="lg">
              <Calendar className="w-5 h-5 mr-2" /> Book Strategy Call
            </BookConsultationButton>
            <a href="tel:+14329349470">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Call (432) 934-9470
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthcareMarketingPage;
